/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISitePickerViewProps } from '@msdyn365-commerce-modules/site-picker';

import { ISiteOptionsData, ISitePickerConfig } from '../../master/definition-extensions/site-picker.ext.props.autogenerated';

interface IExtendedSitePickerViewProps extends Omit<ISitePickerViewProps, 'config'> {
    store: IModuleProps;
    config: ISitePickerConfig;
}

export default (props: IExtendedSitePickerViewProps) => {
    return <>{_renderSitePicker(props)}</>;
};

const trimSlashes = (str: string): string => {
    return str.replace(/^\/+|\/+$/g, '');
};

/**
 * Compares two URL paths to determine if they are equivalent, ignoring leading and trailing slashes.
 * This allows for comparison of paths that might not be exactly the same, e.g., "/cip/de" and "/cip/de/". (user input)
 * Also remove all query params from the destinationUrl that might get added automatically
 *
 * @param destinationUrl - The destination URL to compare.
 * @param sitePath - The current site path to compare.
 * @returns A boolean indicating whether the trimmed and lowercased paths are equivalent.
 */
const comparePaths = (destinationUrl: string | undefined, sitePath: string | undefined): boolean => {
    const path = destinationUrl?.split('?')[0];
    if (!path || !sitePath) return false;

    const trimmedDestinationUrl = trimSlashes(path.toLocaleLowerCase());
    const trimmedSitePath = trimSlashes(sitePath.toLocaleLowerCase());

    return trimmedDestinationUrl === trimmedSitePath;
};

const _renderDesktopView = (
    props: IExtendedSitePickerViewProps,
    currentLanguage?: ISiteOptionsData,
    showShortSiteNameLargeScreen?: boolean
) => {
    const { SitePickerMenu, SitePickerMenuSpan } = props;

    return (
        <>
            <Node {...SitePickerMenu} onClick={props.renderSiteOptions}>
                {showShortSiteNameLargeScreen ? currentLanguage?.shortSiteName : currentLanguage?.siteName}
                <Node {...SitePickerMenuSpan} />
            </Node>
            {props.siteOptionsState ? _renderSiteOptionsList(props, currentLanguage) : null}
        </>
    );
};

const _renderSiteOptionsList = (props: IExtendedSitePickerViewProps, currentLanguage?: ISiteOptionsData): React.ReactNode => {
    const { SitePickerMenuList, SitePickerMenuListItem, SitePickerMenuListItemArray } = props;

    const options = SitePickerMenuListItemArray!
        .filter(item => !(React.isValidElement(item) && comparePaths(currentLanguage?.siteRedirectUrl.destinationUrl, item.props.href)))
        .map((item, index) => (
            <Node {...SitePickerMenuListItem} key={index}>
                {item}
            </Node>
        ));

    return (
        <Node {...SitePickerMenuList} onMouseLeave={props.resetSiteOptionsState}>
            {options}
        </Node>
    );
};

const _renderMobileView = (props: IExtendedSitePickerViewProps, currentLanguage?: ISiteOptionsData): React.ReactNode => {
    const { SitePickerMobileContainer } = props;
    SitePickerMobileContainer.toggleButtonText = currentLanguage?.shortSiteName;

    return (
        <Node className={`ms-site-picker__container`}>
            <Node {...SitePickerMobileContainer} onClick={props.renderSiteOptions}>
                <div>
                    {props.SitePickerMenuListItemArray?.filter(
                        item =>
                            !(React.isValidElement(item) && comparePaths(currentLanguage?.siteRedirectUrl.destinationUrl, item.props.href))
                    ).map((item, index) => {
                        return <div key={index}>{item}</div>;
                    })}
                </div>
            </Node>
        </Node>
    );
};

const _renderSitePicker = (props: IExtendedSitePickerViewProps): React.ReactNode => {
    // Extends the original site picker behavior
    // Checks for current site path value and evaluates set language
    const { sitePath } = props.store.requestContext;
    const { siteOptions, showShortSiteNameLargeScreen } = props.config;

    let defaultLanguage: ISiteOptionsData = {
        siteRedirectUrl: { destinationUrl: sitePath },
        shortSiteName: props.config.heading?.text ?? ''
    };

    let currentLanguage: ISiteOptionsData = {
        ...(siteOptions?.find((option): option is ISiteOptionsData => comparePaths(option.siteRedirectUrl.destinationUrl, sitePath)) ??
            defaultLanguage)
    };

    const { isMobileView, SitePicker } = props;
    return (
        <Module {...SitePicker}>
            {isMobileView
                ? _renderMobileView(props, currentLanguage)
                : _renderDesktopView(props, currentLanguage, showShortSiteNameLargeScreen)}
        </Module>
    );
};
